import { useCallback, useEffect, useState } from 'react';
import type { FlagActivity, FlagPriority, FlagRecord, ZoneId } from '@vigilo/shared';
import { del, get, post, put } from '@vigilo/api-client';
import { useMockMode } from '@/shell/MockModeContext';
import { useApp } from '@/shell/AppContext';
import { MOCK_FLAG_ACTIVITY, MOCK_FLAGS } from '../data/mock';

export interface CreateFlagDto {
  operationId: string;
  zoneId: string;
  behaviourNote: string;
  priority: FlagPriority;
  monitoredZoneIds: string[];
}

export type CreateFlagInput = Omit<CreateFlagDto, 'operationId'> & { operationId?: string };

export interface UpdateFlagDto {
  priority?: FlagPriority;
  behaviourNote?: string;
  monitoredZoneIds?: ZoneId[];
}

export type FlagToast = (message: string, variant?: 'success' | 'warning' | 'error') => void;

const DEFAULT_FLAG_LIMIT = 20;
export const FLAG_LIMIT = configuredFlagLimit();
const MOCK_DELAY_MS = 200;
const FLAG_SESSION = 'session-vigilo-demo-2026';
const DEFAULT_FLAG_POLL_MS = 5000;

interface UseFlagsOptions {
  includeActivity?: boolean;
  pollMs?: number;
}

interface FlagLoadResult {
  flags: FlagRecord[];
  activity?: Record<string, FlagActivity>;
}

const inFlightFlagLoads = new Map<string, Promise<FlagLoadResult>>();

function configuredFlagLimit(): number {
  const parsed = Number.parseInt(import.meta.env.VITE_CROWD_FLAG_LIMIT ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_FLAG_LIMIT;
}

function unwrapList<T>(body: T[] | { data?: T[] }): T[] {
  return Array.isArray(body) ? body : (body.data ?? []);
}

function unwrapItem<T>(body: T | { data?: T }): T {
  return 'data' in (body as { data?: T }) && (body as { data?: T }).data
    ? (body as { data: T }).data
    : body as T;
}

function delay(): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, MOCK_DELAY_MS));
}

async function loadLiveFlags(operationId: string | undefined, includeActivity: boolean): Promise<FlagLoadResult> {
  if (!operationId) throw new Error('operationId is required for live anonymous flags');

  const key = `${operationId}:${includeActivity ? 'with-activity' : 'flags-only'}`;
  const existing = inFlightFlagLoads.get(key);
  if (existing) return existing;

  const request = (async () => {
    const qs = `?operationId=${encodeURIComponent(operationId)}`;
    const body = await get<FlagRecord[] | { data?: FlagRecord[] }>(`/geo-monitoring/flags${qs}`);
    const loadedFlags = unwrapList(body);

    if (!includeActivity || loadedFlags.length === 0) {
      return { flags: loadedFlags };
    }

    const activityEntries = await Promise.all(loadedFlags.map(async flag => {
      const activityBody = await get<FlagActivity | { data?: FlagActivity }>(
        `/geo-monitoring/flags/${flag.flagId}/activity${qs}`,
      );
      return [flag.flagId, unwrapItem(activityBody)] as const;
    }));

    return { flags: loadedFlags, activity: Object.fromEntries(activityEntries) };
  })().finally(() => {
    inFlightFlagLoads.delete(key);
  });

  inFlightFlagLoads.set(key, request);
  return request;
}

function makeTokenRef(): string {
  if (crypto.randomUUID) return crypto.randomUUID();
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
    (Number(c) ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> Number(c) / 4).toString(16)
  );
}

export function useFlags(showToast?: FlagToast, options: UseFlagsOptions = {}) {
  const { isMock } = useMockMode();
  const { selectedOperation } = useApp();
  const includeActivity = options.includeActivity ?? false;
  const pollMs = options.pollMs ?? DEFAULT_FLAG_POLL_MS;
  const [flags, setFlags] = useState<FlagRecord[]>(() => (isMock ? [...MOCK_FLAGS] : []));
  const [activity, setActivity] = useState<Record<string, FlagActivity>>(() => (isMock ? { ...MOCK_FLAG_ACTIVITY } : {}));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    let inFlight = false;

    async function load() {
      if (inFlight) return;
      inFlight = true;
      if (isMock) {
        setFlags([...MOCK_FLAGS]);
        if (includeActivity) setActivity({ ...MOCK_FLAG_ACTIVITY });
        inFlight = false;
        return;
      }

      if (!selectedOperation) {
        setFlags([]);
        setActivity({});
        setError(null);
        inFlight = false;
        return;
      }

      // An event that has not started holds no flags yet (the live list only exists for events that are running or on hold):
      // say so calmly, rather than asking for a list the event cannot have.
      if (selectedOperation.status === 'scheduled') {
        setFlags([]);
        setActivity({});
        setError(null);
        setLoading(false);
        inFlight = false;
        return;
      }

      setLoading(true);
      try {
        const result = await loadLiveFlags(selectedOperation?.id, includeActivity);
        if (!cancelled) {
          setFlags(result.flags);
          setError(null);
        }
        if (!cancelled && result.activity) setActivity(result.activity);
      } catch {
        if (!cancelled) {
          setFlags([]);
          setActivity({});
          setError('Failed to load anonymous flags.');
        }
      } finally {
        if (!cancelled) setLoading(false);
        inFlight = false;
      }
    }

    function scheduleNext() {
      if (cancelled || isMock) return;
      timer = window.setTimeout(() => {
        void load().finally(scheduleNext);
      }, pollMs);
    }

    void load().finally(scheduleNext);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [includeActivity, isMock, pollMs, selectedOperation?.id]);

  const createFlag = useCallback(async (dto: CreateFlagInput): Promise<FlagRecord> => {
    if (flags.length >= FLAG_LIMIT) {
      showToast?.(`Session flag limit reached (${FLAG_LIMIT}/${FLAG_LIMIT})`, 'warning');
      throw new Error('Session flag limit reached');
    }

    if (isMock) {
      await delay();
      const flag: FlagRecord = {
        flagId: `flag-${String(flags.length + 1).padStart(3, '0')}`,
        sessionId: FLAG_SESSION,
        tokenRef: makeTokenRef(),
        priority: dto.priority,
        behaviourNote: dto.behaviourNote,
        monitoredZoneIds: dto.monitoredZoneIds.length > 0 ? dto.monitoredZoneIds : [dto.zoneId],
        createdAt: new Date().toISOString(),
        expiresAt: null,
        createdBy: 'mock-user-001',
      };
      setFlags(prev => [flag, ...prev]);
      setActivity(prev => ({
        ...prev,
        [flag.flagId]: {
          flagId: flag.flagId,
          tokenRef: flag.tokenRef,
          zonesVisited: [],
          currentZoneId: null,
          alertEvents: [],
        },
      }));
      showToast?.('Flag created', 'success');
      return flag;
    }

    const operationId = dto.operationId ?? selectedOperation?.id;
    if (!operationId) {
      showToast?.('Select an Operation before creating a flag', 'warning');
      throw new Error('operationId is required for live anonymous flags');
    }

    const body = await post<FlagRecord | { data?: FlagRecord }>('/geo-monitoring/flags', {
      ...dto,
      operationId,
    });
    const created = unwrapItem(body);
    setFlags(prev => [created, ...prev]);
    showToast?.('Flag created', 'success');
    return created;
  }, [flags.length, isMock, selectedOperation?.id, showToast]);

  const updateFlag = useCallback(async (flagId: string, updates: UpdateFlagDto): Promise<FlagRecord> => {
    if (isMock) {
      await delay();
      let updated: FlagRecord | undefined;
      setFlags(prev => prev.map(flag => {
        if (flag.flagId !== flagId) return flag;
        updated = { ...flag, ...updates };
        return updated;
      }));
      return updated ?? flags.find(flag => flag.flagId === flagId)!;
    }

    if (!selectedOperation?.id) {
      throw new Error('operationId is required for live anonymous flag updates');
    }

    const qs = `?operationId=${encodeURIComponent(selectedOperation.id)}`;
    const body = await put<FlagRecord | { data?: FlagRecord }>(`/geo-monitoring/flags/${flagId}${qs}`, updates);
    const updated = unwrapItem(body);
    setFlags(prev => prev.map(flag => flag.flagId === flagId ? updated : flag));
    return updated;
  }, [flags, isMock, selectedOperation?.id]);

  const removeFlag = useCallback(async (flagId: string): Promise<void> => {
    if (isMock) {
      await delay();
      setFlags(prev => prev.filter(flag => flag.flagId !== flagId));
      setActivity(prev => {
        const next = { ...prev };
        delete next[flagId];
        return next;
      });
      showToast?.('Flag removed', 'success');
      return;
    }

    if (!selectedOperation?.id) {
      throw new Error('operationId is required for live anonymous flag removal');
    }

    const qs = `?operationId=${encodeURIComponent(selectedOperation.id)}`;
    await del(`/geo-monitoring/flags/${flagId}${qs}`);
    setFlags(prev => prev.filter(flag => flag.flagId !== flagId));
    setActivity(prev => {
      const next = { ...prev };
      delete next[flagId];
      return next;
    });
    showToast?.('Flag removed', 'success');
  }, [isMock, selectedOperation?.id, showToast]);

  const getFlagActivity = useCallback(async (flagId: string): Promise<FlagActivity> => {
    const cached = activity[flagId];
    if (cached) return cached;

    if (isMock) {
      await delay();
      const fallback: FlagActivity = {
        flagId,
        tokenRef: flags.find(flag => flag.flagId === flagId)?.tokenRef ?? makeTokenRef(),
        zonesVisited: [],
        currentZoneId: null,
        alertEvents: [],
      };
      setActivity(prev => ({ ...prev, [flagId]: fallback }));
      return fallback;
    }

    if (!selectedOperation?.id) {
      throw new Error('operationId is required for live anonymous flag activity');
    }

    const qs = `?operationId=${encodeURIComponent(selectedOperation.id)}`;
    const body = await get<FlagActivity | { data?: FlagActivity }>(`/geo-monitoring/flags/${flagId}/activity${qs}`);
    const loaded = unwrapItem(body);
    setActivity(prev => ({ ...prev, [flagId]: loaded }));
    return loaded;
  }, [activity, flags, isMock, selectedOperation?.id]);

  const notStarted = !isMock && selectedOperation?.status === 'scheduled';

  return { flags, activity, loading, error, notStarted, createFlag, updateFlag, removeFlag, getFlagActivity };
}
